import ShareDB from 'sharedb/lib/client';
import DocumentWithPresence from './DocumentWithPresence';
import History from './History';
import type CollaborativeDocument from './CollaborativeDocument';
import { DocumentState } from './CollaborativeDocument';
import {
  type CollaborationConfiguration,
  type EditorPlugin,
  type OperationFilter,
} from '../types/aha-editor';
import handleErrors from './handleErrors';
import type { Operation } from 'slate';

interface CollaborationPluginAPI {
  init: () => void;
  isInitialized: () => boolean;
  disconnect: () => void;
  openDocument: (
    documentCollection: string,
    documentId: string,
    initialEditor: any,
    initialVersion: any,
    onDocumentStateChange: any,
    onUpdate: any
  ) => DocumentWithPresence;
  connectDocument: (collaborativeDocument: CollaborativeDocument) => void;
  closeDocument: (
    collaborativeDocument: CollaborativeDocument,
    callback?: (err: any) => void
  ) => void;
  DocumentState: typeof DocumentState;
  canApply: (collaborativeDocument: CollaborativeDocument) => boolean;
  apply: (
    collaborativeDocument: CollaborativeDocument,
    change: {
      operations: Operation[];
    }
  ) => void;
}

const connections = new WeakMap();
const errorHandlers = {};

export class HistoryUtils {
  static undo(editor) {
    History.getForEditor(editor)?.undo(editor);
  }

  static redo(editor) {
    History.getForEditor(editor)?.redo(editor);
  }
}

function addErrorHandler(collection, id, handler) {
  if (!handler) return;
  if (!errorHandlers[collection]) {
    errorHandlers[collection] = {};
  }

  errorHandlers[collection][id] = handler;
}

function removeErrorHandler(collection, id) {
  if (errorHandlers[collection]) {
    delete errorHandlers[collection][id];
  }
}

// Available options:
//  - getCollaborationServer: A function that returns a WebSocket connected to the ShareDB server
//  - getFayeClient: A function that returns a faye client, for syncing cursors
//  - defaultBlock: The default block type to use for invalid documents  (defaults to 'paragraph')
const CollaborationPlugin = (options: CollaborationConfiguration) => {
  options = options || {};
  let connection;
  let fayeClient = null;
  let socket;
  let onError: CollaborationConfiguration['onError'] | null = null;
  const onConnect = options.onConnect || (() => {});
  let openDocumentOnConnect;
  let initialized = false;

  const init = () => {
    if (options.onError) {
      onError = options.onError;
    }

    fayeClient = options.getFayeClient();
    socket = options.getCollaborationServer();

    connection = connections.get(socket);

    if (connection) {
      onConnect();
      initialized = true;
      return;
    }

    connection = new ShareDB.Connection(socket);
    connections.set(socket, connection);

    // These IDs don't need to be private, since they're part of the
    // selection broadcast to other users. They just need a low
    // chance of collision.
    handleErrors(connection, errorHandlers);
    onConnect();
    initialized = true;
  };

  const disconnect = () => {
    connection.close();
    connection = null;
    connections.delete(socket);
    socket = null;
  };

  const isInitialized = () => initialized;

  const openDocument = (
    documentCollection: string,
    documentId: string,
    initialEditor,
    initialVersion,
    onDocumentStateChange,
    onUpdate
  ): DocumentWithPresence => {
    addErrorHandler(documentCollection, documentId, onError);

    const collaborativeDocument = new DocumentWithPresence({
      connection,
      collection: documentCollection,
      documentId,
      fayeClient,
    });

    if (onDocumentStateChange) {
      collaborativeDocument.onDocumentStateChange(onDocumentStateChange);
    }

    if (onUpdate) {
      collaborativeDocument.onUpdate(onUpdate);
    }

    collaborativeDocument.load(initialEditor, initialVersion);
    return collaborativeDocument;
  };

  const connectDocument = (collaborativeDocument: CollaborativeDocument) => {
    if (openDocumentOnConnect) {
      connection.removeListener('connected', openDocumentOnConnect);
    }

    openDocumentOnConnect = () => {
      collaborativeDocument.connect(() => {});
    };

    if (connection.state === 'connected') {
      openDocumentOnConnect();
    } else {
      connection.once('connected', openDocumentOnConnect);
    }
  };

  const closeDocument: CollaborationPluginAPI['closeDocument'] = (
    collaborativeDocument: CollaborativeDocument,
    callback?: (err: any) => void
  ) => {
    const { collection, documentId } = collaborativeDocument;

    if (connection && openDocumentOnConnect) {
      connection.removeListener('connected', openDocumentOnConnect);
    }

    collaborativeDocument.close((err) => {
      removeErrorHandler(collection, documentId);
      callback && callback(err);
    });
  };

  const canApply = (collaborativeDocument: CollaborativeDocument) =>
    collaborativeDocument?.canAcceptChanges;

  const apply = (
    collaborativeDocument: CollaborativeDocument,
    change: { operations: Operation[] }
  ) => {
    if (canApply(collaborativeDocument)) {
      collaborativeDocument.submitChange(change);
    }
  };

  const historyFilter: OperationFilter = (editor, operation, next) => {
    const history = History.getForEditor(editor);
    if (history) {
      history.save(editor, operation);
    }

    next(editor, operation);
  };

  const plugin: EditorPlugin<CollaborationPluginAPI> = {
    name: 'Collaboration plugin',
    operationFilter: historyFilter,
    api: {
      init,
      isInitialized,
      disconnect,
      openDocument,
      connectDocument,
      closeDocument,
      DocumentState,
      canApply,
      apply,
    },
  };

  return plugin;
};

export default CollaborationPlugin;
